import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, MailCheck } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { NovaLogo } from "@/components/landing/Logo";

export default function ForgotPasswordPage() {
  const { API } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" data-testid="forgot-password-page">
      <div className="hidden lg:flex relative flex-col justify-between bg-nova-navy text-white p-12 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-nova-blue/20 blur-3xl" />
        <div className="absolute inset-0 bg-grid-navy opacity-[0.15]" />
        <div className="relative"><NovaLogo inverted /></div>
        <p className="relative text-[10px] font-semibold tracking-[0.32em] text-blue-400">A NEW WAY FORWARD.</p>
      </div>

      <div className="flex items-center justify-center px-5 sm:px-10 py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-md">
          <Link to="/login" data-testid="forgot-password-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-nova-muted hover:text-nova-navy transition-colors mb-8">
            <ArrowLeft size={15} /> Back to sign in
          </Link>

          {sent ? (
            <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-8" data-testid="forgot-password-sent-message">
              <MailCheck size={34} className="text-emerald-600" />
              <h1 className="mt-5 text-2xl font-bold tracking-tighter text-nova-navy">Check your inbox</h1>
              <p className="mt-3 text-sm text-emerald-800 leading-relaxed">
                If an account exists for that email and is eligible for password reset, we've sent password reset instructions.
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold tracking-tighter text-nova-navy">Forgot password?</h1>
              <p className="mt-2 text-sm text-nova-slate">Enter your account email and we'll send you a secure reset link.</p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="forgot-password-form">
                <div>
                  <label htmlFor="forgot-email" className="block text-xs font-semibold text-nova-navy mb-1.5">Work email</label>
                  <input
                    id="forgot-email"
                    data-testid="forgot-password-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourbusiness.com"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                  />
                </div>

                {error && (
                  <p data-testid="forgot-password-error" className="text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</p>
                )}

                <motion.button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-nova-blue px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300 disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Send reset link
                  {!loading && <ArrowRight size={15} />}
                </motion.button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
